/**
 * PackRelatedStrip — the related-packs strip below the hero (pack page copy spec §3, state A
 * + state D). New component (narcis_funnel plan, Step 1 deliverable): state A shows the
 * pack-authored/auto-picked sibling packs with a live catalog count ("the catalog finally
 * visible from a landing"); state D re-skins the same four cards as one-click invocations
 * ("More styles your model can wear"). B/C states render nothing here — per canvas 02, the
 * related strip only appears in the resting and owner states; B/C lead with the queue/training
 * story instead.
 *
 * Card data is resolved server-side (relatedPacks.ts's pure resolvers + the catalog/registry
 * join) and passed in fully built — this component is presentation only, no data fetching.
 */
import {Link} from 'react-router'
import styles from './PackRelatedStrip.module.scss'

export interface PackRelatedCard {
    slug: string
    title: string
    cover: string
    /** Pre-formatted meta line — "9 looks · lifestyle" (state A) or "wear this · ~90s" (state D). */
    meta: string
}

interface PackRelatedStripProps {
    title: string
    linkLabel: string
    linkHref: string
    cards: PackRelatedCard[]
    /** Invitation 2 (spine réclame, "The four invitations" canvas) — D-del cards gain an inline
     *  "Wear this →" affordance carrying the same `?autogenerate=1` contract the hub/Model Home
     *  suggestion cards already use. Card and affordance are two separate links (not a nested
     *  anchor) — each `<a>` targets a different destination, `/{slug}` for the card body and
     *  `/{slug}?autogenerate=1` for the affordance, so this can't collapse into one link without
     *  losing the fire-generation-and-navigate behavior. Only ever passed for heroState 'D' — A's
     *  cards (browsing, no model to fire) keep the plain single-link render, byte-identical. */
    showWearAction?: boolean
}

export function PackRelatedStrip({title, linkLabel, linkHref, cards, showWearAction = false}: PackRelatedStripProps) {
    if (cards.length === 0) return null
    return (
        <section className={styles.rel} aria-label="Related packs">
            <div className={styles.relHead}>
                <h2 className={styles.relTitle}>{title}</h2>
                <Link to={linkHref} className={styles.relLink}>{linkLabel}</Link>
            </div>
            <div className={styles.relGrid}>
                {cards.map((card) => (
                    <div key={card.slug} className={styles.relCard}>
                        <Link to={`/${card.slug}`} className={styles.relCardLink}>
                            <span className={styles.relCardPh}>
                                <img src={card.cover} alt="" loading="lazy" className={styles.relCardImg} />
                            </span>
                            <span className={styles.relCardTitle}>
                                {card.title}
                                <i className={styles.relCardMeta}>{card.meta}</i>
                            </span>
                        </Link>
                        {showWearAction && (
                            <Link to={`/${card.slug}?autogenerate=1`} className={styles.relCardWear}>
                                Wear this →
                            </Link>
                        )}
                    </div>
                ))}
            </div>
        </section>
    )
}
